import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ErrorPage } from '@screentone/addon-auth-wrapper';

import { ResponsiveLoader, WidePageWrapper } from 'components';
import { CopyPublishedURL } from 'components/context-menu-actions/copy-published-url/CopyPublishedURL';
import ExternalLink, { ExternalLinkContentType } from 'components/context-menu-actions/external-link/ExternalLink';
import { OpenInCMS } from 'components/context-menu-actions/open-in-cms/OpenInCMS';
import { OpenInNewsGrid } from 'components/context-menu-actions/open-in-newsgrid/OpenInNewsGrid';
import { RemoveEntity } from 'components/context-menu-actions/remove-entity/RemoveEntity';
import { ViewPublishedURL } from 'components/context-menu-actions/view-published-url/ViewPublishedURL';
import { PageRootContainer } from 'components/page-root-container/PageRootContainer';
import { AllessehContentUpdateProvider } from 'contexts/allesseh-content-update/AllessehContentUpdateContext';
import {
  ContextMenuActionsProvider,
  MenuActions,
  MenuActionsKeys
} from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { DataModelProvider, DataModelType } from 'contexts/datamodel/DataModelContext';
import { DragAndDropProvider } from 'contexts/drag-and-drop/DragAndDropContext';
import { SummarianProvider } from 'contexts/summarian/SummarianContext';
import { TrashProvider } from 'contexts/trash/TrashContext';
import { ArticleItem, MultiCollectionDto, useMultiCollectionDtoByAllessehIdQuery } from 'data/generated/graphql';
import { MultiCollectionEdit } from 'features/multicollection-edit/MultiCollectionEdit';
import { useConvertedProperty } from 'hooks';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { reportError } from 'utils/errorBoundary';
import { safelyParseContent } from 'utils/temp';

const MultiCollectionEditPage = () => {
  const { allessehId } = useParams();
  const currentProperty = useConvertedProperty();
  const [multiCollectionDTO, setMultiCollectionDTO] = useState<MultiCollectionDto | null>(null);
  const [fetchError, setFetchError] = useState<string>('');

  const {
    data,
    isFetching,
    error: loadingError
  } = useMultiCollectionDtoByAllessehIdQuery(
    { allessehId: allessehId!, publicationKey: currentProperty ?? '' },
    { enabled: !!allessehId && !!currentProperty, cacheTime: 0 }
  );

  useEffect(() => {
    if (loadingError) {
      reportError('Error fetching collection data: ', loadingError as Error);
      let error = '500';
      if (loadingError instanceof Error) {
        if (loadingError.message === 'Not found') {
          error = '404';
        }
        if (loadingError.message === 'Failed to fetch') {
          error = '503';
        }
      }
      setFetchError(error);
    }
  }, [loadingError]);

  useLayoutEffect(() => {
    if (data?.multiCollectionDTOByAllessehId) {
      setMultiCollectionDTO(data.multiCollectionDTOByAllessehId as MultiCollectionDto);
    }
  }, [data]);

  const contextActions = useMemo<MenuActions>(
    () => ({
      [MenuActionsKeys.DraftArticle]: [
        {
          component: ({ hierarchyId }) => <RemoveEntity hierarchyId={hierarchyId!} text="Remove from collection" />,
          withDivider: 'bottom'
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInCMS content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInNewsGrid content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <CopyPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            const content = safelyParseContent<AllessehContent>(article);

            return (
              <ExternalLink
                contentId={content.data.id}
                isPublished={!!content.data.attributes.published_datetime}
                type={ExternalLinkContentType.ARTICLE}
                embargo={content.data.attributes.content_status === 'embargo'}
              />
            );
          }
        }
      ],
      [MenuActionsKeys.ArticleSearch]: [
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInCMS content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInNewsGrid content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <CopyPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            const content = safelyParseContent<AllessehContent>(article);

            return (
              <ExternalLink
                contentId={content.data.id}
                isPublished={!!content.data.attributes.published_datetime}
                type={ExternalLinkContentType.ARTICLE}
                embargo={content.data.attributes.content_status === 'embargo'}
              />
            );
          }
        }
      ]
    }),
    []
  );

  if (fetchError) {
    return <ErrorPage type={String(fetchError)} />;
  }

  return (
    <PageRootContainer noBottomMargin>
      {isFetching ? (
        <ResponsiveLoader />
      ) : !multiCollectionDTO ? (
        <ErrorPage type="500" />
      ) : (
        <AllessehContentUpdateProvider>
          <SummarianProvider>
            <DataModelProvider
              type={DataModelType.MultiCollectionDTO}
              root={multiCollectionDTO.root}
              metadata={multiCollectionDTO.metadata}
              childRefs={multiCollectionDTO.childRefs}
            >
              <TrashProvider>
                <DragAndDropProvider>
                  <ContextMenuActionsProvider actions={contextActions}>
                    <WidePageWrapper>
                      <MultiCollectionEdit />
                    </WidePageWrapper>
                  </ContextMenuActionsProvider>
                </DragAndDropProvider>
              </TrashProvider>
            </DataModelProvider>
          </SummarianProvider>
        </AllessehContentUpdateProvider>
      )}
    </PageRootContainer>
  );
};

export default MultiCollectionEditPage;
